import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney, fmtNumber } from "@/lib/format";
import { Scales } from "@phosphor-icons/react";

/**
 * Exchange Receivables grouped by evaluator. Mirror of FinancierReport
 * for the exchange side — same column structure, same KPI strip.
 */
export default function EvaluatorReport({ dateFrom, dateTo, branchId, branchTypeId, modelCategory }) {
  const [data, setData] = useState({ rows: [], totals: null });
  const [loading, setLoading] = useState(false);
  const [pendingOnly, setPendingOnly] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (branchId) params.branch_id = branchId;
        if (branchTypeId) params.branch_type_id = branchTypeId;
        if (modelCategory) params.model_category = modelCategory;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        const { data: res } = await api.get(
          "/accounts/exchange-receivables/by-evaluator",
          { params }
        );
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [branchId, branchTypeId, modelCategory, dateFrom, dateTo]);

  const rows = useMemo(() => {
    return (data.rows || []).filter(
      (r) => r.files_outstanding > 0 || r.received_count > 0
    );
  }, [data.rows]);
  const visible = useMemo(
    () => (pendingOnly ? rows.filter((r) => r.files_outstanding > 0) : rows),
    [rows, pendingOnly]
  );
  const totals = data.totals || {
    files_outstanding: 0, pending_amount: 0, short_amount: 0,
    outstanding_amount: 0,
  };

  return (
    <div className="space-y-4" data-testid="evaluator-report">
      <div className="flex flex-wrap items-end gap-3">
        <label className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-ink-muted cursor-pointer select-none">
          <input
            type="checkbox"
            checked={pendingOnly}
            onChange={(e) => setPendingOnly(e.target.checked)}
            data-testid="evaluator-report-pending-only"
            className="w-4 h-4"
          />
          Show only evaluators with outstanding files
        </label>
      </div>

      <div className="grid grid-cols-4 gap-3" data-testid="evaluator-report-kpis">
        <Tile label="Outstanding files" value={fmtNumber(totals.files_outstanding)}
              testId="ekpi-files-out" />
        <Tile label="Pending ₹ (no recv)" value={fmtMoney(totals.pending_amount)}
              testId="ekpi-pending-amt" accent="danger" />
        <Tile label="Short ₹ (partial)" value={fmtMoney(totals.short_amount)}
              testId="ekpi-short-amt" accent="warn" />
        <Tile label="Total outstanding ₹" value={fmtMoney(totals.outstanding_amount)}
              testId="ekpi-out-amt" accent="danger" />
      </div>

      {loading ? (
        <div className="bg-canvas-card border border-canvas-line p-10 text-center font-mono text-xs text-ink-subtle">
          <Scales size={20} weight="bold" className="inline-block animate-pulse" />
          <div className="mt-2">Crunching evaluators…</div>
        </div>
      ) : (
        <div className="border border-canvas-line bg-canvas-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="evaluator-report-table">
            <thead className="bg-canvas-panel">
              <tr className="text-left font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">
                <th className="px-3 py-2.5">Evaluator</th>
                <th className="px-3 py-2.5 text-right">Files (out)</th>
                <th className="px-3 py-2.5 text-right">Pending ₹</th>
                <th className="px-3 py-2.5 text-right">Short ₹</th>
                <th className="px-3 py-2.5 text-right">Outstanding ₹</th>
                <th className="px-3 py-2.5 text-right">0-30d</th>
                <th className="px-3 py-2.5 text-right">31-60</th>
                <th className="px-3 py-2.5 text-right">61-90</th>
                <th className="px-3 py-2.5 text-right">90+</th>
                <th className="px-3 py-2.5 text-right">Received ₹</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-ink-subtle font-mono text-xs">
                  No evaluators match the current filters.
                </td></tr>
              )}
              {visible.map((r) => (
                <tr key={r.evaluator_name}
                    data-testid={`evaluator-row-${r.evaluator_name.replace(/\s+/g, "-").toLowerCase()}`}
                    className="border-t border-canvas-line hover:bg-canvas-panel/40 transition-colors">
                  <td className="px-3 py-2.5 font-medium">{r.evaluator_name}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-bold">
                    {fmtNumber(r.files_outstanding)}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-destructive">
                    {r.pending_amount > 0 ? fmtMoney(r.pending_amount) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-amber-700">
                    {r.short_amount > 0 ? fmtMoney(r.short_amount) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-bold text-destructive">
                    {fmtMoney(r.outstanding_amount)}
                  </td>
                  {["0-30", "31-60", "61-90", "90+"].map((b) => (
                    <td key={b} className="px-3 py-2.5 text-right tabular-nums text-ink-muted text-xs">
                      {(r.aging?.[b]?.count || 0) > 0
                        ? `${r.aging[b].count}×${fmtMoney(r.aging[b].amount)}`
                        : "—"}
                    </td>
                  ))}
                  <td className="px-3 py-2.5 text-right tabular-nums text-[#00713a]">
                    {fmtMoney(r.received_amount)}
                  </td>
                </tr>
              ))}
            </tbody>
            {visible.length > 0 && (
              <tfoot className="bg-canvas-panel font-bold">
                <tr className="border-t-2 border-ink">
                  <td className="px-3 py-2.5">TOTAL ({visible.length} evaluators)</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {fmtNumber(visible.reduce((s, r) => s + r.files_outstanding, 0))}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-destructive">
                    {fmtMoney(visible.reduce((s, r) => s + r.pending_amount, 0))}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-amber-700">
                    {fmtMoney(visible.reduce((s, r) => s + r.short_amount, 0))}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-destructive">
                    {fmtMoney(visible.reduce((s, r) => s + r.outstanding_amount, 0))}
                  </td>
                  {["0-30", "31-60", "61-90", "90+"].map((b) => (
                    <td key={b} className="px-3 py-2.5 text-right tabular-nums text-xs">
                      {fmtMoney(visible.reduce((s, r) => s + (r.aging?.[b]?.amount || 0), 0))}
                    </td>
                  ))}
                  <td className="px-3 py-2.5 text-right tabular-nums text-[#00713a]">
                    {fmtMoney(visible.reduce((s, r) => s + r.received_amount, 0))}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, accent, testId }) {
  const cls = accent === "danger" ? "border-destructive bg-destructive/5 text-destructive"
    : accent === "warn" ? "border-amber-500 bg-amber-50 text-amber-700"
    : "border-canvas-line";
  return (
    <div className={`border-2 ${cls} p-4`} data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
        {label}
      </div>
      <div className="font-display text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}
